import styled, { css } from 'styled-components';

interface ContainerProps {
  collapsed?: boolean;
}

const collapsedStyles = css`
  width: 60px;
  align-items: center;
  padding: 12px 0;
  gap: 6px;

  .icon-button {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: none;
    background: none;
    color: #4a5568;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #f1f5f9;
    }

    i {
      font-size: 22px;
    }

    .logo-icon {
      width: 26px;
      height: 22px;
      object-fit: contain;
    }
  }

  .sidebar-footer-collapsed {
    margin-top: auto;

    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #cbd5e0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      color: #fff;
      font-weight: 600;
    }
  }
`;

export const Container = styled.div<ContainerProps>`
  width: 300px;
  height: 100%;
  background-color: #fff;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  padding: 0;
  flex-shrink: 0;

  ${(props) => props.collapsed && collapsedStyles}

  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;

    .help-video-button {
      font-size: 11px;
      flex-shrink: 0;

      i {
        font-size: 16px;
      }
    }

    .logo-area {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;
      font-size: 15px;
      color: #2d3748;

      .logo-icon {
        width: 26px;
        height: 22px;
        object-fit: contain;
      }
    }

    .new-chat-icon {
      cursor: pointer;
      color: #718096;
      font-size: 20px;
      background: none;
      border: none;
      padding: 4px;
      border-radius: 6px;
      transition: background 0.2s;

      &:hover {
        background: #e2e8f0;
      }
    }
  }

  .search-box {
    padding: 0 16px 12px;

    .search-input {
      width: 100%;
      padding: 10px 16px;
      border: 1px solid #e2e8f0;
      border-radius: 999px;
      font-size: 14px;
      background: #fff;
      color: #4a5568;
      outline: none;
      display: flex;
      align-items: center;
      gap: 8px;

      &:focus-within {
        border-color: #2b6a4e;
      }

      i {
        font-size: 18px;
        color: #a0aec0;
      }

      input {
        border: none;
        outline: none;
        flex: 1;
        font-size: 14px;
        background: transparent;
      }

      .arrow {
        color: #2a2a2a;
      }
    }
  }

  .sidebar-section {
    padding: 4px 16px;

    .section-label {
      font-size: 11px;
      font-weight: 600;
      color: #a0aec0;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }
  }

  .sidebar-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #4a5568;
    transition: background 0.2s;
    border: none;
    background: none;
    width: 100%;
    text-align: left;

    &:hover {
      background: #e2e8f0;
    }

    i {
      font-size: 18px;
      color: #718096;
    }
  }

  .folder-block {
    margin-bottom: 2px;
  }

  .folder-row {
    border-radius: 8px;
    transition: background 0.2s, box-shadow 0.15s, outline 0.15s;

    &:hover {
      background: #f1f5f9;
    }

    &.drop-target {
      background: #f0faf5;
      outline: 2px dashed #2b6a4e;
      outline-offset: -2px;
    }

    .folder-actions {
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
      padding-right: 6px;
    }

    &:hover .folder-actions,
    &.menu-open .folder-actions {
      opacity: 1;
    }

    .folder-action-btn {
      color: #718096;

      &:hover {
        color: #2d3748;
      }

      i {
        font-size: 18px;
      }
    }
  }

  .folder-chats {
    padding: 4px 0 4px 24px;

    .folder-empty {
      font-size: 12px;
    }

    .folder-chat-item {
      font-size: 13px;
      padding: 4px 12px;
    }
  }

  .history-section.drop-target {
    background: #f0faf5;
    outline: 2px dashed #2b6a4e;
    outline-offset: -2px;
    border-radius: 8px;
  }

  .history-list {
    flex: 1;
    overflow-y: auto;
    padding: 0 16px;
    transition: background 0.15s, outline 0.15s;

    &.drop-target {
      background: #f0faf5;
      outline: 2px dashed #2b6a4e;
      outline-offset: -2px;
      border-radius: 8px;
    }

    .history-item {
      padding: 6px 12px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 13px;
      color: #4a5568;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: background 0.2s;

      &:hover {
        background: #e2e8f0;
      }
    }
  }

  .chat-row {
    border-radius: 6px;
    transition: background 0.2s, opacity 0.2s;
    cursor: grab;

    &.dragging {
      opacity: 0.4;
      cursor: grabbing;
    }

    &:hover {
      background: #f1f5f9;
    }

    .chat-actions {
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
      padding-right: 6px;
    }

    &:hover .chat-actions,
    &.menu-open .chat-actions {
      opacity: 1;
    }

    .chat-action-btn {
      color: #718096;

      &:hover {
        color: #2d3748;
      }

      i {
        font-size: 18px;
      }
    }

    .history-item,
    .folder-chat-item {
      min-width: 0;
    }
  }

  .sidebar-footer {
    padding: 16px;
    border-top: 1px solid #e9ecef;
    background: #f0faf5;
    display: flex;
    align-items: center;
    gap: 10px;

    .settings-icon {
      cursor: pointer;
      color: #718096;
      font-size: 18px;
      background: none;
      border: none;
    }
  }
`;
